/* ============================================================================
   Fifth Room — "Recent projects" carousel  (BESPOKE, update-safe)

   Powers the <fr-recent-projects> element in sections/fr-recent-projects.liquid.

   The feel: the row holds still for a few seconds, then glides exactly one slide
   to the left, holds again, and repeats forever — a perpetual stepped loop.

   How it works
   ------------
   1. Clone the whole slide set once and append it → the track is two identical
      halves. The loop becomes seamless: when the scroll position passes the width
      of one set (the "period"), we subtract the period instantly. Because the
      clones are pixel-identical to the originals, the jump is invisible.
   2. Auto-advance is a requestAnimationFrame timer (NOT a CSS keyframe animation):
      every "dwell" ms it programmatically scrolls the viewport by one slide width
      with smooth behaviour. rAF keeps the timer alive on iOS under Low Power Mode,
      where CSS/Web-Animations stall (the same lesson learned in fr-partners.js).
   3. It pauses on hover (mouse), while pressed (touch / drag), on keyboard focus,
      and when scrolled off-screen. A visible pause / play button (WCAG 2.2.2) lets
      anyone stop it for good. Under prefers-reduced-motion it never auto-advances
      — it's just a normal swipeable / keyboard-scrollable row.

   Video handling — four hard-won iOS lessons (this section is in the footer group,
   so it renders on EVERY page; eager video here costs every single page view):

   a. ONLY rebuild on a real WIDTH change. iOS Safari fires `resize` every time the
      address bar collapses/expands on scroll — and you MUST scroll to reach a footer
      section, so an unguarded resize tore down and re-cloned all 7 <video> elements
      mid-scroll, each restarting a fresh stream. DevTools emulation has no collapsing
      URL bar, which is exactly why this only ever reproduced on a real iPhone.
   b. NOTHING loads until the section is near the viewport (the "arm" observer), and
      then only the tiles in / next to the scroller — capped, and unloaded again once
      far away. iOS caps concurrent media elements well below the 14 this DOM holds
      (7 originals + 7 clones), and past the cap it simply shows posters.
   c. Always re-set `v.muted = true` before play(): a cloneNode'd <video> can lose the
      muted PROPERTY in WebKit even though the attribute copies → autoplay rejected.
   d. Distinguish NotAllowedError (the UA genuinely refused — Low Power Mode / Low Data
      Mode → reveal the tap-to-play button) from AbortError (our own pause() raced an
      in-flight play(), which is routine while scrolling and must NOT flash a button).

   Pure DOM + matchMedia + IntersectionObserver, no @theme imports — a Horizon
   upstream update can't break it.
   ========================================================================== */

(function () {
  'use strict';

  var REDUCED = '(prefers-reduced-motion: reduce)';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  class FrRecentProjects extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-fr-rp-viewport]');
      this.track = this.querySelector('[data-fr-rp-track]');
      if (!this.viewport || !this.track) return;

      this.originals = Array.prototype.slice.call(this.track.children);
      if (!this.originals.length) return;

      // "Hold time per slide" (seconds) → ms. Whole cycle = glide (~0.4s) + hold.
      var dwell = parseFloat(this.getAttribute('data-dwell'));
      this.dwellMs = (isNaN(dwell) ? 4 : Math.min(8, Math.max(2, dwell))) * 1000;
      this.pauseOnHover = this.hasAttribute('data-pause-on-hover');

      this.pitch = 0;
      this.period = 0;
      this.canLoop = false;
      this.running = false;
      this.raf = null;
      this.lastStep = null;
      this.wrapping = false;

      // Pause flags — auto-advance runs only when every one is clear.
      this.hoverPaused = false;
      this.pressPaused = false;
      this.focusPaused = false;
      this.stopped = false;            // explicit pause via the button (set below)
      this.userStarted = false;        // did the visitor opt in to motion themselves?
      this.visible = true;             // section on-screen?

      this.inView = new Set();         // videos on-screen within the scroller → play
      this.nearBy = new Set();         // videos in the wider ring → worth loading
      this.videos = [];
      this.armed = false;              // has the section come near the viewport yet?
      this.built = false;              // has a successful measure/clone run happened?
      this.builtW = -1;                // viewport width the current clone set was built at

      this.smoothOK = 'scrollBehavior' in document.documentElement.style;
      this.mqReduce = window.matchMedia(REDUCED);
      this.reducedMotion = this.mqReduce.matches;

      // Under Reduce Motion the slideshow starts PAUSED rather than the toggle being
      // hidden, so the existing control becomes an opt-in "Play project slideshow"
      // (its glyph already flips to a triangle on aria-pressed="true"). Nobody gets
      // motion they didn't ask for, but anyone who wants it — including on a phone
      // with Reduce Motion on system-wide — is one tap away.
      // NB: must come AFTER reducedMotion is read, not with the pause flags above.
      this.stopped = this.reducedMotion;

      this.toggle = this.closest('.fr-rp')
        ? this.closest('.fr-rp').querySelector('[data-fr-rp-toggle]')
        : null;

      // Bind handlers once so add/removeEventListener pair up.
      this._loop = this.loop.bind(this);
      this._onScroll = this.onScroll.bind(this);
      this._onReduce = this.onReduceChange.bind(this);
      this._rebuild = debounce(this.build.bind(this), 200);

      this.lastW = window.innerWidth;

      this.bindEvents();
      this.observeVisibility();
      this.observeArm();

      // Measure after layout (one frame), then again on load / font swap.
      requestAnimationFrame(this.build.bind(this));
      window.addEventListener('load', this._rebuild);
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this._rebuild);
    }

    disconnectedCallback() {
      this.stop();
      window.removeEventListener('load', this._rebuild);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
      if (this.viewport) this.viewport.removeEventListener('scroll', this._onScroll);
      if (this.mqReduce) this.mqReduce.removeEventListener('change', this._onReduce);
      if (this._io) this._io.disconnect();
      if (this._vio) this._vio.disconnect();
      if (this._rio) this._rio.disconnect();
      if (this._arm) this._arm.disconnect();
      if (this._resumeTimer) clearTimeout(this._resumeTimer);
      // iOS is slow to GC detached media elements — free the decoders explicitly.
      // (`videos` is undefined if connectedCallback bailed before setup.)
      if (this.videos) {
        for (var i = 0; i < this.videos.length; i++) this.unload(this.videos[i]);
      }
    }

    /* ── Build / measure / clone ───────────────────────────────────────── */
    build() {
      // Belt-and-braces to the width-only resize guard: `load` and `fonts.ready` also
      // fire _rebuild, and re-cloning an already-correct track would needlessly bin
      // every loaded <video>. Rebuild only when the width genuinely moved.
      var w = this.viewport.clientWidth;
      if (this.built && w === this.builtW) { this.refreshToggle(); return; }

      this.stop();
      this.teardownClones();
      this.canLoop = false;

      var laidOut = this.measure();

      // Bind videos on EVERY path, looping or not: a row short enough to fit without
      // overflow still has clips to play, and there's no `autoplay` attribute left to
      // cover for us if we skip this.
      this.bindVideos();
      this.refreshToggle();
      this.updateRunState();

      if (laidOut) { this.built = true; this.builtW = w; }
    }

    /* Measure and (if the row overflows) clone. Returns false only when the track
       isn't laid out yet, so `load` / `fonts.ready` / resize can retry. */
    measure() {
      if (this.originals.length < 2) return true;   // nothing to loop, but laid out

      var first = this.originals[0];
      var second = this.originals[1];
      var last = this.originals[this.originals.length - 1];

      // One slide's pitch (centre-to-centre) and the originals' total span.
      var pitch = second.offsetLeft - first.offsetLeft;
      var setWidth = (last.offsetLeft + last.offsetWidth) - first.offsetLeft;
      if (pitch <= 0 || setWidth <= 0) return false; // not laid out yet → retry later

      // Nothing to loop if every slide already fits without overflow.
      if (setWidth <= this.viewport.clientWidth + 1) return true;

      var styles = getComputedStyle(this.track);
      var gap = parseFloat(styles.columnGap || styles.gap) || 0;

      var frag = document.createDocumentFragment();
      for (var i = 0; i < this.originals.length; i++) {
        frag.appendChild(this.cloneItem(this.originals[i]));
      }
      this.track.appendChild(frag);

      this.pitch = pitch;
      this.period = setWidth + gap;    // distance from original[0] to clone[0]
      this.canLoop = true;

      // Keep any prior scroll position within the new period.
      if (this.viewport.scrollLeft >= this.period) {
        this.viewport.scrollLeft = this.viewport.scrollLeft % this.period;
      }
      return true;
    }

    teardownClones() {
      var kids = Array.prototype.slice.call(this.track.children);
      for (var i = 0; i < kids.length; i++) {
        if (!kids[i].dataset || !kids[i].dataset.frClone) continue;
        // Release the media element before detaching — iOS holds the decoder open on
        // detached <video>s far longer than you'd expect.
        var vids = kids[i].querySelectorAll('video');
        for (var j = 0; j < vids.length; j++) this.unload(vids[j]);
        this.track.removeChild(kids[i]);
      }
      this.inView.clear();
      this.nearBy.clear();
    }

    cloneItem(node) {
      var clone = node.cloneNode(true);
      clone.dataset.frClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      // Keep duplicated slides out of the tab order / a11y tree.
      var foc = clone.querySelectorAll('a, video, button, [tabindex]');
      for (var i = 0; i < foc.length; i++) foc[i].setAttribute('tabindex', '-1');
      return clone;
    }

    /* ── The stepped auto-advance ──────────────────────────────────────── */
    loop(ts) {
      if (!this.running) return;
      if (this.lastStep == null) this.lastStep = ts;
      if (ts - this.lastStep >= this.dwellMs) {
        this.step();
        this.lastStep = ts;
      }
      this.raf = requestAnimationFrame(this._loop);
    }

    step() {
      if (!this.canLoop) return;
      var target = this.viewport.scrollLeft + this.pitch;
      if (this.smoothOK) {
        try {
          this.viewport.scrollTo({ left: target, behavior: 'smooth' });
          return;
        } catch (e) { /* fall through to instant */ }
      }
      this.viewport.scrollLeft = target;
    }

    /* Seamless wrap: once we've scrolled one whole set in, rewind by a period.
       The clones make the rewind pixel-identical, so it's invisible. */
    onScroll() {
      if (!this.canLoop || this.wrapping) return;
      if (this.viewport.scrollLeft >= this.period) {
        this.wrapping = true;
        this.viewport.scrollLeft -= this.period;
        this.wrapping = false;
      }
    }

    /* Is motion allowed right now? Either the visitor has no Reduce Motion preference,
       or they have one but explicitly pressed play — consented motion is fine. */
    motionOK() {
      return !this.reducedMotion || this.userStarted;
    }

    updateRunState() {
      var should = this.canLoop && this.motionOK() && this.visible &&
        !this.hoverPaused && !this.pressPaused && !this.focusPaused && !this.stopped;
      if (should && !this.running) this.start();
      else if (!should && this.running) this.stop();
      this.syncVideos();
    }

    start() {
      if (this.running || !this.canLoop) return;
      this.running = true;
      this.lastStep = null;
      this.raf = requestAnimationFrame(this._loop);
    }

    stop() {
      this.running = false;
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    /* ── Events ────────────────────────────────────────────────────────── */
    bindEvents() {
      var self = this;

      this.viewport.addEventListener('scroll', this._onScroll, { passive: true });
      // ⚠️ WIDTH-only. iOS Safari fires `resize` on every URL-bar collapse/expand while
      // scrolling — and this section lives in the footer, so scrolling to it is
      // guaranteed. An unguarded rebuild here destroyed and re-created every cloned
      // <video> mid-scroll (lesson (a) in the header). Height changes never affect
      // this horizontal layout, so ignore them.
      this._onResize = function () {
        if (window.innerWidth === self.lastW) return;
        self.lastW = window.innerWidth;
        self._rebuild();
      };
      window.addEventListener('resize', this._onResize);
      this.mqReduce.addEventListener('change', this._onReduce);

      // Hover (mouse only) → pause.
      if (this.pauseOnHover) {
        this.viewport.addEventListener('mouseenter', function () { self.hoverPaused = true; self.updateRunState(); });
        this.viewport.addEventListener('mouseleave', function () { self.hoverPaused = false; self.updateRunState(); });
      }

      // Press / drag (touch + mouse) → pause; resume shortly after release so
      // momentum scrolling can settle before we take over again.
      this.viewport.addEventListener('pointerdown', function () {
        if (self._resumeTimer) { clearTimeout(self._resumeTimer); self._resumeTimer = null; }
        self.pressPaused = true;
        self.updateRunState();
      });
      var release = function () {
        if (self._resumeTimer) clearTimeout(self._resumeTimer);
        self._resumeTimer = setTimeout(function () {
          self.pressPaused = false;
          self.updateRunState();
        }, 700);
      };
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);

      // Keyboard focus anywhere inside → pause.
      this.viewport.addEventListener('focusin', function () { self.focusPaused = true; self.updateRunState(); });
      this.viewport.addEventListener('focusout', function () { self.focusPaused = false; self.updateRunState(); });

      // Pause / play button (WCAG 2.2.2). Under Reduce Motion this starts in the
      // "play" state and pressing it is the visitor's explicit opt-in to motion.
      if (this.toggle) {
        this.toggle.addEventListener('click', function () {
          self.stopped = !self.stopped;
          if (!self.stopped) self.userStarted = true;   // consented → motionOK() opens up
          self.syncToggleLabel();
          self.updateRunState();
        });
      }
    }

    onReduceChange(e) {
      this.reducedMotion = e.matches;
      // Switching the preference ON mid-session revokes any earlier opt-in and parks
      // the slideshow; switching it OFF hands control back to normal autoplay.
      if (e.matches) { this.userStarted = false; this.stopped = true; }
      else { this.stopped = false; }
      this.refreshToggle();
      this.updateRunState();
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      var self = this;
      this._io = new IntersectionObserver(function (entries) {
        self.visible = entries[entries.length - 1].isIntersecting;
        self.updateRunState();
      }, { threshold: 0 });
      this._io.observe(this);
    }

    /* The arm gate: not one video byte is fetched until the section is within ~400px
       of the viewport. This section renders on EVERY page, so without this the whole
       site pays for footer video on every single page view. Same fire-once idiom as
       vendor assets/product-recommendations.js. */
    observeArm() {
      var self = this;
      if (!('IntersectionObserver' in window)) { this.armed = true; return; }
      this._arm = new IntersectionObserver(function (entries, obs) {
        if (!entries[entries.length - 1].isIntersecting) return;
        obs.disconnect();
        self.armed = true;
        self.syncVideos();
      }, { rootMargin: '0px 0px 400px 0px', threshold: 0 });
      this._arm.observe(this);
    }

    /* The pause/play button only makes sense while the row can actually
       auto-advance (enough slides + motion allowed). */
    refreshToggle() {
      if (!this.toggle) return;
      // Autoplaying video is auto-moving content in its own right, so a row that has
      // clips but doesn't scroll still needs a pause control (WCAG 2.2.2).
      var hasVideo = !!(this.videos && this.videos.length);
      // Shown under Reduce Motion too — there it reads "Play project slideshow" and is
      // how a visitor opts in to rotation, rather than a dead row with no control.
      var available = this.canLoop || hasVideo;
      this.toggle.hidden = !available;
      if (!available) this.stopped = false;
      this.syncToggleLabel();
    }

    syncToggleLabel() {
      if (!this.toggle) return;
      // aria-pressed="true" == paused; the CSS already swaps the glyph to a triangle.
      this.toggle.setAttribute('aria-pressed', this.stopped ? 'true' : 'false');
      var label = this.toggle.querySelector('.fr-rp__toggle-text');
      if (label) label.textContent = this.stopped ? 'Play project slideshow' : 'Pause project slideshow';
    }

    /* ── Videos ─────────────────────────────────────────────────────────────
       Two rings, both scoped to the scroller:
         • nearBy (±75% of the viewport) → worth having loaded, cheaply.
         • inView (≥25% visible)         → actually play.
       Leaving nearBy unloads the element, which is what keeps the loaded count
       near the handful of tiles on screen instead of creeping to all 14. */
    bindVideos() {
      this.videos = Array.prototype.slice.call(this.track.querySelectorAll('video'));
      if (this._vio) this._vio.disconnect();
      if (this._rio) this._rio.disconnect();
      this.bindPlayButtons();
      if (!this.videos.length || !('IntersectionObserver' in window)) return;
      var self = this;
      var i;

      this._rio = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (entries[k].isIntersecting) self.nearBy.add(entries[k].target);
          else self.nearBy.delete(entries[k].target);
        }
        self.syncVideos();
      }, { root: this.viewport, rootMargin: '0px 75% 0px 75%', threshold: 0 });

      this._vio = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (entries[k].isIntersecting) self.inView.add(entries[k].target);
          else self.inView.delete(entries[k].target);
        }
        self.syncVideos();
      }, { root: this.viewport, threshold: 0.25 });

      for (i = 0; i < this.videos.length; i++) {
        this._rio.observe(this.videos[i]);
        this._vio.observe(this.videos[i]);
      }
    }

    /* Give the <video> its src for the first time. Mirrors assets/video-background.js:
       the URL rides on data-fr-rp-src until we decide the tile is worth loading. */
    ensureLoaded(v, hint) {
      if (v.preload !== hint) v.preload = hint;
      if (v.frLoaded) return;
      var srcs = v.querySelectorAll('source');
      var got = false;
      for (var i = 0; i < srcs.length; i++) {
        var u = srcs[i].getAttribute('data-fr-rp-src');
        if (u) { srcs[i].setAttribute('src', u); got = true; }
      }
      if (!got) return;
      v.frLoaded = true;
      v.load();
    }

    /* Reset the element: frees both the buffered bytes and, crucially, the iOS decoder. */
    unload(v) {
      if (!v || !v.frLoaded) return;
      try { v.pause(); } catch (e) {}
      v.classList.remove('is-playing');
      var srcs = v.querySelectorAll('source');
      for (var i = 0; i < srcs.length; i++) srcs[i].removeAttribute('src');
      v.removeAttribute('src');
      v.preload = 'none';
      v.frLoaded = false;
      v.frPlayReq = false;
      try { v.load(); } catch (e) {}
    }

    tryPlay(v) {
      this.ensureLoaded(v, 'auto');
      if (!v.frLoaded || v.frPlayReq || !v.paused) return;
      v.muted = true;                  // clones can lose the muted property → iOS refuses
      var self = this;
      var p = v.play();
      if (!p || !p.then) return;
      v.frPlayReq = true;
      p.then(function () {
        v.frPlayReq = false;
        v.frBlocked = false;
        v.classList.add('is-playing');
        self.refreshButtons();
      }).catch(function (err) {
        v.frPlayReq = false;
        var name = err && err.name;
        // AbortError = our own pause() raced this play() — routine while scrolling.
        // Only a real refusal should surface a button.
        if (name === 'NotAllowedError') v.frBlocked = true;
        else if (name === 'NotSupportedError') v.frErrored = true;
        else return;
        self.refreshButtons();
      });
    }

    syncVideos() {
      if (!this.videos || !this.videos.length || !this.armed) return;
      // `stopped` is the WCAG 2.2.2 pause button — it must stop the videos too, not
      // just the scroll (it previously only stopped the scroll).
      var allowAuto = this.visible && this.motionOK() && !this.stopped;
      for (var i = 0; i < this.videos.length; i++) {
        var v = this.videos[i];
        var near = this.nearBy.has(v);
        var on = this.inView.has(v);

        if (!near) { this.unload(v); continue; }

        var wantAuto = on && allowAuto && !v.frBlocked && !v.frErrored && !v.frUserPaused;
        var wantUser = on && v.frUserPlay && this.visible && !this.stopped;

        if (wantAuto || wantUser) {
          this.tryPlay(v);
        } else if (on) {
          // On-screen but deliberately not playing (reduced motion / blocked / stopped):
          // don't spend bytes, just let the poster stand and offer the button.
          try { v.pause(); } catch (e) {}
        } else {
          // In the ring but off-screen: prime metadata only so the next tile starts fast.
          this.ensureLoaded(v, 'metadata');
          try { v.pause(); } catch (e) {}
        }
      }
      this.refreshButtons();
    }

    /* The button appears when a tile genuinely cannot autoplay: the UA refused (Low
       Power Mode / Low Data Mode) or motion is reduced. It STAYS mounted while the
       clip plays — faded out via CSS, but still hit-testable and still keyboard
       focusable — because a looping clip with no way to stop it fails WCAG 2.2.2.
       That was the "no way to pause" bug. */
    refreshButtons() {
      for (var i = 0; i < this.videos.length; i++) {
        var v = this.videos[i];
        var btn = v.frButton;
        if (!btn) continue;
        var on = this.inView.has(v) && this.visible;
        // Offer a control whenever this tile isn't simply autoplaying on its own:
        // refused, motion-reduced, or the visitor has taken manual control either way.
        var manual = v.frBlocked || !this.motionOK() || v.frUserPlay || v.frUserPaused;
        var playing = !v.paused && !v.frErrored;
        btn.hidden = !(on && manual && !v.frErrored);
        // Faded (but present) while playing; solid when the tile is showing its poster.
        btn.classList.toggle('fr-rp__play--playing', playing);
        var label = btn.querySelector('.visually-hidden');
        if (label && btn.frLabel !== playing) {
          btn.frLabel = playing;
          label.textContent = (playing ? 'Pause video' : 'Play video') + (btn.dataset.frCaption ? ': ' + btn.dataset.frCaption : '');
        }
      }
    }

    /* One place decides play-vs-pause, so the button and the tile tap agree. */
    toggleVideo(v) {
      if (!v.paused) {
        v.frUserPlay = false;
        // Sticky: without this the very next scroll tick would see an on-screen tile
        // with autoplay allowed and immediately restart the clip the visitor just
        // stopped. Cleared the moment they press play again.
        v.frUserPaused = true;
        try { v.pause(); } catch (e) {}
      } else {
        v.frUserPlay = true;           // consented motion
        v.frUserPaused = false;
        v.frBlocked = false;
        this.ensureLoaded(v, 'auto');
        v.muted = true;
        // Synchronous inside the gesture handler: that user activation is what gets
        // us past an iOS Low Power Mode autoplay block.
        var self = this;
        var p = v.play();
        if (p && p.then) {
          p.then(function () { v.classList.add('is-playing'); self.refreshButtons(); })
           .catch(function (e) {
             if (e && e.name === 'NotAllowedError') v.frBlocked = true;
             self.refreshButtons();
           });
        }
      }
      this.refreshButtons();
    }

    bindPlayButtons() {
      var self = this;
      for (var i = 0; i < this.videos.length; i++) {
        (function (v) {
          if (v.frBtnBound) return;
          var item = v.closest ? v.closest('.fr-rp__item') : null;
          var btn = item ? item.querySelector('[data-fr-rp-play]') : null;
          if (!btn) return;
          v.frButton = btn;
          v.frBtnBound = true;
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();       // don't double-fire the tile handler below
            self.toggleVideo(v);
          });

          // Tap anywhere on the tile to pause / resume. Skipped when the merchant has
          // put a link on this slide — there a tap must navigate, so the button (which
          // sits outside the <a>) stays the control.
          var link = item.querySelector('.fr-rp__link');
          if (!link) {
            var media = item.querySelector('.fr-rp__media');
            if (media) {
              media.style.cursor = 'pointer';
              media.addEventListener('click', function () { self.toggleVideo(v); });
            }
          } else {
            btn.classList.add('fr-rp__play--persistent');
          }
          // Keep the poster showing the moment playback stops for any reason.
          v.addEventListener('pause', function () { v.classList.remove('is-playing'); self.refreshButtons(); });
          v.addEventListener('emptied', function () { v.classList.remove('is-playing'); });
        })(this.videos[i]);
      }
    }
  }

  if (!customElements.get('fr-recent-projects')) {
    customElements.define('fr-recent-projects', FrRecentProjects);
  }
})();
